import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { checkEmailInAllCollections } from './utils/emailValidator';

function RegistrationSelection() {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [existingAccounts, setExistingAccounts] = useState([]);
    const [isCheckingAccounts, setIsCheckingAccounts] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                setIsCheckingAccounts(true);
                
                try {
                    // Check for existing accounts
                    const accounts = await checkEmailInAllCollections(currentUser.email);
                    setExistingAccounts(accounts);
                    console.log('Found existing accounts:', accounts);
                } catch (error) {
                    console.error('Error checking existing accounts:', error);
                } finally {
                    setIsCheckingAccounts(false);
                    setIsLoading(false);
                }
            } else {
                console.log("No user authenticated - redirecting to auth");
                navigate('/auth');
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    const registrationOptions = [
        {
            title: "Institutional Portfolios",
            description: "Private Strategy Partnerships and Quantitative Asset Management.",
            icon: "📈",
            route: "/investregistration",
            features: ["Bespoke Algorithmic Execution", "High-Water Mark agreements", "Strict Risk Management", "Verified Track Record"],
            color: "from-blue-600 to-purple-600",
            accountType: "investment",
            dashboardRoute: "/investdashboard"
        },
        {
            title: "Algo Trading Academy",
            description: "The premier pipeline from beginner to professional quant.",
            icon: "🎓",
            route: "/registration",
            features: ["Python & MQL5 Mastery", "ONNX ML pipelines", "Live Capital Pathways", "Performance-based earnings"],
            color: "from-purple-600 to-pink-600",
            accountType: "training",
            dashboardRoute: "/academy/dashboard"
        }
    ];

    const handleSignOut = async () => {
        try {
            await auth.signOut();
            localStorage.clear();
            navigate('/auth');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const handleServiceSelection = (option) => {
        if (!user) {
            navigate('/auth');
            return;
        }

        // Handle legacy pooled funds differently
        if (option.accountType === 'legacy') {
            navigate('/contact-us');
            return;
        }

        // Check if user already has this type of account
        const hasAccount = existingAccounts.find(account => account.type === option.accountType);

        if (hasAccount) {
            // User already has this account - ask if they want to go to dashboard
            const goToDashboard = window.confirm(
                `You already have a ${option.title} account.\n\nWould you like to go to your dashboard instead?`
            );

            if (goToDashboard) {
                // Set session data for dashboard
                localStorage.setItem('userType', hasAccount.type);
                localStorage.setItem('userId', hasAccount.data?.userId || hasAccount.id);
                if (user.email) localStorage.setItem('userEmail', user.email);

                navigate(option.dashboardRoute);
            }
            return;
        }

        // User doesn't have this account - proceed to registration
        navigate(option.route);
    };

    const getAccountTypeDisplay = (type) => {
        switch(type) {
            case 'investment': return 'PAM & MAM Services';
            case 'training': return 'Training Program';
            case 'general': return 'Investment Account';
            case 'legacy': return 'Legacy Pooled Portfolios';
            default: return type;
        }
    };

    const isAccountTypeExisting = (accountType) => {
        return existingAccounts.some(account => account.type === accountType);
    };

    // Show loading spinner while checking authentication
    if (isLoading || isCheckingAccounts) {
        return (
            <div className="min-h-screen  flex items-center justify-center">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center"
                >
                    <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-white text-lg">
                        {isCheckingAccounts ? 'Checking your existing accounts...' : 'Verifying your account...'}
                    </p>
                </motion.div>
            </div>
        );
    }

    // If no user after loading, this shouldn't render (but just in case)
    if (!user) {
        return (
            <div className="min-h-screen  flex items-center justify-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20"
                >
                    <h2 className="text-2xl font-bold text-white mb-4">Authentication Required</h2>
                    <p className="text-gray-300 mb-6">You need to sign in to access this page.</p>
                    <button
                        onClick={() => navigate('/auth')}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-xl font-bold hover:shadow-xl transition-all"
                    >
                        Go to Sign In
                    </button>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen  flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-6xl w-full"
            >
                <div className="text-center mb-12">
                    <motion.h1 
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-5xl font-bold text-white mb-6"
                    >
                        Choose Your Path
                    </motion.h1>
                    <motion.p 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-gray-300 text-xl max-w-2xl mx-auto"
                    >
                        Welcome to KSQuants, {user.displayName || user.email?.split('@')[0]}! Select the service that best matches your trading and investment goals.
                    </motion.p>
                </div>

                {/* Existing Accounts Notice */}
                {existingAccounts.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8 p-6 bg-blue-500/20 border border-blue-500/30 rounded-2xl backdrop-blur-md"
                    >
                        <h3 className="text-xl font-bold text-blue-300 mb-3 flex items-center">
                            <span className="mr-2">🎯</span>
                            Your Existing Accounts
                        </h3>
                        <div className="grid md:grid-cols-3 gap-4">
                            {existingAccounts.map((account, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 + 0.3 }}
                                    className="bg-green-500/20 border border-green-500/30 rounded-lg p-4"
                                >
                                    <div className="text-green-300 font-semibold">
                                        ✅ {getAccountTypeDisplay(account.type)}
                                    </div>
                                    <div className="text-green-200 text-sm mt-1">
                                        Active Account
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                        <div className="mt-4">
                            <button
                                onClick={() => navigate('/dashboard-selection')}
                                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all font-semibold"
                            >
                                📊 Access My Existing Accounts
                            </button>
                        </div>
                    </motion.div>
                )}

                <div className="grid md:grid-cols-2 gap-8 mb-12 max-w-4xl mx-auto">
                    {registrationOptions.filter(option => !isAccountTypeExisting(option.accountType)).length > 0 ? (
                        registrationOptions
                            .filter(option => !isAccountTypeExisting(option.accountType))
                            .map((option, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.15 }}
                                    whileHover={{ scale: 1.05, y: -10 }}
                                    onClick={() => handleServiceSelection(option)}
                                    className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 cursor-pointer transition-all duration-500 group relative hover:bg-white/20"
                                >
                                    <div className="text-center">
                                        <motion.div 
                                            className="text-6xl mb-6"
                                            whileHover={{ scale: 1.2, rotate: 5 }}
                                            transition={{ type: "spring", stiffness: 300 }}
                                        >
                                            {option.icon}
                                        </motion.div>
                                        
                                        <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-blue-300 transition-colors">
                                            {option.title}
                                        </h3>
                                        
                                        <p className="text-gray-300 mb-6 leading-relaxed">
                                            {option.description}
                                        </p>
                                        
                                        <div className="space-y-3 mb-8">
                                            {option.features.map((feature, idx) => (
                                                <motion.div 
                                                    key={idx}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: (index * 0.15) + (idx * 0.1) + 0.3 }}
                                                    className="text-gray-300 text-sm flex items-center justify-center"
                                                >
                                                    <span className="text-green-400 mr-3 text-lg">✓</span>
                                                    {feature}
                                                </motion.div>
                                            ))}
                                        </div>
                                        
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className={`w-full bg-gradient-to-r ${option.color} text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition-all duration-300`}
                                        >
                                            Get Started
                                        </motion.button>
                                    </div>
                                </motion.div>
                            ))
                    ) : (
                        <div className="col-span-full text-center py-12">
                            <h3 className="text-2xl font-bold text-white mb-2">You're All Set!</h3>
                            <p className="text-gray-300">You currently have access to all our available services.</p>
                        </div>
                    )}
                </div>

                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="text-center space-y-4"
                >
                    <p className="text-gray-400 text-sm">
                        Need help deciding? <a href="/contact" className="text-blue-400 hover:text-blue-300 underline">Contact our team</a>
                    </p>
                    
                    <div className="flex justify-center space-x-6">
                        <button
                            onClick={() => navigate('/')}
                            className="text-blue-400 hover:text-blue-300 underline text-sm transition-colors"
                        >
                            ← Back to Home
                        </button>
                        
                        {existingAccounts.length > 0 && (
                            <button
                                onClick={() => navigate('/dashboard-selection')}
                                className="text-green-400 hover:text-green-300 underline text-sm transition-colors"
                            >
                                📊 My Accounts
                            </button>
                        )}
                        
                        <button
                            onClick={handleSignOut}
                            className="text-red-400 hover:text-red-300 underline text-sm transition-colors"
                        >
                            Sign out
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
}

export default RegistrationSelection;